import { useSuspenseQuery } from '@tanstack/react-query';
import first from 'lodash/first';
import { fetchCurrentCompanyUser } from '~/api/company-users';
import { ErrorCodes, ErrorResponse } from '~/client/backend';
import { useActiveCompanyId } from '~/hooks/useActiveCompany';
import { useSuspenseLoggedUser } from '~/services/user';
import { CompanyQueryKeys } from '~/types/company';
import { isAxiosError } from '~/utils/helpers';

const isNoActiveSubscriptionError = (error: unknown) =>
  isAxiosError<ErrorResponse>(error) &&
  error.response?.status === 403 &&
  first(error.response.data.errors)?.code === ErrorCodes.NoActiveSubscription;

/**
 * Suspense hook returning the company-user of the logged user for the active company.
 * Mirrors `useActiveCompany`; consumers render inside the same Suspense boundaries.
 */
export const useActiveCompanyUser = () => {
  const companyId = useActiveCompanyId();
  const { data: user } = useSuspenseLoggedUser();

  const { data: companyUser } = useSuspenseQuery({
    queryKey: CompanyQueryKeys.companyUser(companyId, user.id),
    queryFn: async () => {
      try {
        return await fetchCurrentCompanyUser(companyId, user.id);
      } catch (error) {
        // With an expired subscription the backend rejects company-users by design;
        // feature resolution must keep working (all flags off) instead of crashing
        // pages like /expired-subscription that render outside an active subscription.
        if (isNoActiveSubscriptionError(error)) return null;
        throw error;
      }
    },
    staleTime: Infinity,
  });

  return companyUser;
};
